// Leaflet distribution: shared by the Leaflets page's tabs.
//
// payFor and COLOURS mirror garageFarmServer helpers/leaflets.js. The server
// works the pay out again when a round is approved and its number is the one
// kept; this copy is for showing what a round will pay before it's approved.
import { daysBetween, ymd } from './trade';

// Each person's colour on the map, handed out in this order (the dataviz
// reference order without red, which marks clashes). Zones are always
// labelled with the person's name, so colour is never the only clue.
export const COLOURS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6'];
export const CLASH_COLOUR = '#d03b3b';
export const FREE_COLOUR = '#6b7280';   // zone with nobody on it
export const ROUTE_COLOUR = '#113449';  // suggested walking line
export const OUTLINE_COLOUR = '#2e7d32'; // a zone being drawn, and on printed sheets

export const APP_URL = 'https://leaflets.teardrophydro.co.uk';

export const PERSON_STATUSES = [
  { key: 'trial',  label: 'Trial',  tone: 'blue', hint: 'Doing a trial job first' },
  { key: 'active', label: 'Active', tone: 'ok',   hint: 'Taking jobs' },
  { key: 'paused', label: 'Paused', tone: 'warn', hint: 'Not taking jobs for now' },
  { key: 'left',   label: 'Left',   tone: 'bad',  hint: 'Can\'t log in any more; their history is kept' },
];
export const PERSON_STATUS = Object.fromEntries(PERSON_STATUSES.map(s => [s.key, s]));

export const JOB_STATUS = {
  active:    { label: 'In progress', tone: 'blue' },
  done:      { label: 'Done',        tone: 'ok' },
  cancelled: { label: 'Cancelled',   tone: undefined },
};

// A round's state: waiting to be checked, approved (and paid or not), or sent back.
export const RUN_STATE = {
  submitted: { label: 'To check',  tone: 'warn' },
  approved:  { label: 'Approved',  tone: 'blue' },
  paid:      { label: 'Paid',      tone: 'ok' },
  rejected:  { label: 'Sent back', tone: 'bad' },
};
export const runState = (run) => (run.status === 'approved' && run.payout ? 'paid' : run.status);

export const PAY_METHODS = [
  { key: 'bank_transfer', label: 'Bank transfer' },
  { key: 'cash',          label: 'Cash' },
  { key: 'other',         label: 'Other' },
];

export const COVERED_DAYS = 56; // a zone delivered in the last 8 weeks counts as covered

// ── Money and numbers ─────────────────────────────────────────────────────────

export const payFor = (delivered, ratePer1000) =>
  Math.round((Number(delivered) * Number(ratePer1000)) / 10) / 100;

const pence = (n) => Math.round(Number(n || 0) * 100);

export const formatCount = (n) => Number(n || 0).toLocaleString('en-GB');

export function formatMinutes(m) {
  if (m === null || m === undefined || m === '') return '';
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h ? `${h}h ${String(min).padStart(2, '0')}m` : `${min}m`;
}

// Leaflets approved, waiting to be checked and still to deliver.
export function jobProgress(job) {
  let approved = 0;
  let waiting = 0;
  for (const r of job.runs || []) {
    if (r.status === 'approved') approved += r.delivered;
    else if (r.status === 'submitted') waiting += r.delivered;
  }
  return { approved, waiting, left: Math.max(0, job.leaflets - approved - waiting) };
}

// One person's money: earned (approved), paid, owed (approved, unpaid) and
// what's waiting to be checked, at each job's rate.
export function personMoney(personId, jobs) {
  let earned = 0, paid = 0, owed = 0, waiting = 0, owedRounds = 0;
  for (const job of jobs) {
    if (job.distributor !== personId) continue;
    for (const r of job.runs || []) {
      if (r.status === 'approved') {
        earned += pence(r.pay);
        if (r.payout) paid += pence(r.pay);
        else { owed += pence(r.pay); owedRounds += 1; }
      } else if (r.status === 'submitted') {
        waiting += pence(payFor(r.delivered, job.ratePer1000));
      }
    }
  }
  return { earned: earned / 100, paid: paid / 100, owed: owed / 100, waiting: waiting / 100, owedRounds };
}

// How someone's doing: approved leaflets, rounds, and speed where they logged
// their time and distance.
export function personStats(personId, jobs) {
  let delivered = 0, rounds = 0, timedLeaflets = 0, minutes = 0, km = 0, kmLeaflets = 0;
  for (const job of jobs) {
    if (job.distributor !== personId) continue;
    for (const r of job.runs || []) {
      if (r.status !== 'approved') continue;
      delivered += r.delivered;
      rounds += 1;
      if (r.minutes > 0) { minutes += r.minutes; timedLeaflets += r.delivered; }
      if (r.distanceKm > 0) { km += r.distanceKm; kmLeaflets += r.delivered; }
    }
  }
  return {
    delivered,
    rounds,
    perHour: minutes ? Math.round((timedLeaflets / minutes) * 60) : null,
    perKm: km ? Math.round(kmLeaflets / km) : null,
  };
}

export const perHour = (run) => (run.minutes > 0 ? Math.round((run.delivered / run.minutes) * 60) : null);
export const perKm = (run) => (run.distanceKm > 0 ? Math.round(run.delivered / run.distanceKm) : null);

// ── Zones ─────────────────────────────────────────────────────────────────────

// The most recent day an approved round was delivered in a zone.
export function lastCovered(zoneId, jobs) {
  let last = '';
  for (const job of jobs) {
    if (job.zone !== zoneId) continue;
    for (const r of job.runs || []) {
      if (r.status === 'approved' && ymd(r.date) > last) last = ymd(r.date);
    }
  }
  return last || null;
}

// 'assigned' (someone has it now), 'covered' (delivered lately) or 'free'.
export function zoneState(zone, jobs, today) {
  const active = jobs.filter(j => j.zone === zone._id && j.status === 'active');
  const last = lastCovered(zone._id, jobs);
  if (active.length) return { state: 'assigned', active, last };
  if (last && daysBetween(last, today) <= COVERED_DAYS) return { state: 'covered', active, last };
  return { state: 'free', active, last };
}

// ── Geometry ──────────────────────────────────────────────────────────────────
// Points are [lat, lng]. At the scale of a town, treating them as flat x/y is
// plenty for "do these two zones overlap?".

export function boundsOf(points) {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const [lat, lng] of points) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

export function pointInPolygon([lat, lng], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [ai, bi] = polygon[i];
    const [aj, bj] = polygon[j];
    if ((bi > lng) !== (bj > lng) && lat < ((aj - ai) * (lng - bi)) / (bj - bi) + ai) inside = !inside;
  }
  return inside;
}

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

// Proper crossings only: edges that merely touch (zones drawn against each
// other, sharing a corner) don't count.
export function segmentsCross(p1, p2, q1, q2) {
  const d1 = cross(q1, q2, p1);
  const d2 = cross(q1, q2, p2);
  const d3 = cross(p1, p2, q1);
  const d4 = cross(p1, p2, q2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function polygonsOverlap(a, b) {
  if (!a?.length || !b?.length) return false;
  const ba = boundsOf(a);
  const bb = boundsOf(b);
  if (ba.maxLat <= bb.minLat || bb.maxLat <= ba.minLat || ba.maxLng <= bb.minLng || bb.maxLng <= ba.minLng) return false;
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      if (segmentsCross(a1, a2, b[j], b[(j + 1) % b.length])) return true;
    }
  }
  // No edges cross: either apart, or one sits wholly inside the other
  return pointInPolygon(a[0], b) || pointInPolygon(b[0], a) || samePolygon(a, b);
}

const samePolygon = (a, b) => a.length === b.length && a.every((p, i) => p[0] === b[i][0] && p[1] === b[i][1]);

// Every overlapping pair among the zones given: [{ a, b }]
export function overlappingPairs(zones) {
  const out = [];
  for (let i = 0; i < zones.length; i++) {
    for (let j = i + 1; j < zones.length; j++) {
      if (polygonsOverlap(zones[i].polygon, zones[j].polygon)) out.push({ a: zones[i], b: zones[j] });
    }
  }
  return out;
}

// Two people working the same streets at once: the same zone, or overlapping
// zones, both with an active job held by different people.
export function clashes(zones, jobs) {
  const active = jobs.filter(j => j.status === 'active');
  const holders = (zoneId) => new Set(active.filter(j => j.zone === zoneId).map(j => j.distributor));
  const out = [];
  for (const zone of zones) {
    if (holders(zone._id).size > 1) out.push({ a: zone, b: zone });
  }
  for (const { a, b } of overlappingPairs(zones)) {
    const ha = holders(a._id);
    const hb = holders(b._id);
    if (ha.size && hb.size && [...ha, ...hb].some(p => !ha.has(p) || !hb.has(p))) out.push({ a, b });
  }
  return out;
}

// Rough area in km², for the zone list.
export function areaKm2(polygon) {
  if (!polygon?.length) return 0;
  const lat0 = (boundsOf(polygon).minLat * Math.PI) / 180;
  const kx = 111.32 * Math.cos(lat0);
  const ky = 110.57;
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    sum += polygon[j][1] * kx * polygon[i][0] * ky - polygon[i][1] * kx * polygon[j][0] * ky;
  }
  return Math.abs(sum) / 2;
}

// ── People ────────────────────────────────────────────────────────────────────

// "Sarah Jones" -> "sarahj"
export function suggestUsername(name) {
  const parts = String(name || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9 ]/g, '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  const u = parts.length > 1 ? parts[0] + parts[parts.length - 1][0] : parts[0];
  return u.slice(0, 32).padEnd(3, '1');
}

// The message to send someone with their login, ready to paste into WhatsApp.
export function loginMessage(person, password) {
  const first = String(person.name || '').trim().split(/\s+/)[0] || 'there';
  return `Hi ${first}, here's your login for the TearDrop leaflets app: ${APP_URL}\n`
    + `Username: ${person.username}\nPassword: ${password}\n`
    + 'It shows your zone on a map, and it\'s where you send the screenshot of your route after each round.';
}

// ── Guidelines ────────────────────────────────────────────────────────────────
// Plain text where "- " starts a bullet and any other line is a heading.
export function parseGuidelines(text) {
  const blocks = [];
  for (const raw of String(text || '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('- ')) {
      if (!blocks.length) blocks.push({ heading: '', bullets: [] });
      blocks[blocks.length - 1].bullets.push(line.slice(2).trim());
    } else {
      blocks.push({ heading: line, bullets: [] });
    }
  }
  return blocks;
}

// Rounds waiting to be checked, oldest first, with their job.
export function roundsToCheck(jobs) {
  const out = [];
  for (const job of jobs) for (const run of job.runs || []) if (run.status === 'submitted') out.push({ job, run });
  return out.sort((x, y) => String(x.run.createdAt).localeCompare(String(y.run.createdAt)));
}
